import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Countdown from "@/components/Countdown";
import { Calendar, Clock, MapPin, Users, ArrowRight } from "lucide-react";

export default function NextEncounter() {
  const [events, setEvents] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/events/public").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const nextEdicao = useMemo(() => {
    const eds = events.filter((e) => (e.event_type || "edicao") === "edicao");
    return eds.find((e) => e.is_next) || eds.find((e) => e.status !== "past");
  }, [events]);

  const futureEncontros = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return events
      .filter((e) => (e.event_type || "edicao") === "encontro" && e.status !== "past" && new Date(e.start_date) >= today)
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [events]);

  const editions = events.filter((e) => (e.event_type || "edicao") === "edicao");
  const encontrosTitle = futureEncontros.length === 1 ? "Próximo Encontro" : "Próximos Encontros";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="label">Agenda</div>
      <h1 className="font-display text-4xl sm:text-6xl font-black mt-2">Prepare o coração.</h1>

      {nextEdicao && (
        <div className="ci-card p-6 sm:p-10 mt-10">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="label">Edição</div>
              <div className="font-display text-3xl font-extrabold mt-1">{nextEdicao.edition_number}ª EDIÇÃO</div>
            </div>
            <span className={`text-xs font-bold tracking-widest px-3 py-1.5 rounded-full ${nextEdicao.registrations_open ? "bg-blue-500/15 text-blue-300 border border-blue-500/40" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
              {nextEdicao.registrations_open ? "INSCRIÇÕES ABERTAS" : "ENCERRADAS"}
            </span>
          </div>
          <h2 className="font-display text-2xl sm:text-4xl font-bold mt-6">{nextEdicao.name}</h2>
          <p className="text-slate-300 mt-3">{nextEdicao.description}</p>
          <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2 text-slate-300"><Calendar size={16} className="text-blue-400" /> {new Date(nextEdicao.start_date).toLocaleDateString("pt-BR")}{nextEdicao.end_date && ` → ${new Date(nextEdicao.end_date).toLocaleDateString("pt-BR")}`}</div>
            {nextEdicao.time && <div className="flex items-center gap-2 text-slate-300"><Clock size={16} className="text-blue-400" /> {nextEdicao.time}</div>}
            {nextEdicao.location && <div className="flex items-center gap-2 text-slate-300"><MapPin size={16} className="text-blue-400" /> {nextEdicao.location}</div>}
          </div>
          <div className="mt-10">
            <div className="label text-center mb-4">Contagem Regressiva</div>
            <Countdown target={`${nextEdicao.start_date}T${(nextEdicao.time || "19:30").padEnd(5, "0")}:00`} />
          </div>
          <div className="mt-10 text-center">
            <button data-testid="next-page-rsvp" onClick={() => nav("/confirmar-presenca")} disabled={!nextEdicao.registrations_open} className="btn-primary disabled:opacity-50">
              CONFIRMAR PRESENÇA <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {futureEncontros.length > 0 && (
        <div className="mt-16" data-testid="next-encontros-section">
          <div className="label">Encontros</div>
          <h2 data-testid="next-encontros-title" className="font-display text-3xl sm:text-5xl font-black mt-2">{encontrosTitle}</h2>
          <div className={`grid gap-5 mt-8 ${futureEncontros.length === 1 ? "" : "md:grid-cols-2"}`}>
            {futureEncontros.map((e) => {
              const target = `${e.start_date}T${(e.time || "19:30").padEnd(5, "0")}:00`;
              const d = new Date(e.start_date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
              return (
                <article key={e.id} data-testid={`next-encontro-${e.id}`} className="ci-card p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="label">Encontro</div>
                      <h3 className="font-display text-xl sm:text-2xl font-bold mt-2">{e.name}</h3>
                    </div>
                    <span className="text-[10px] font-bold tracking-widest px-2 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 shrink-0">ENCONTRO</span>
                  </div>
                  {e.description && <p className="text-slate-300 mt-3 text-sm leading-relaxed">{e.description}</p>}
                  <div className="mt-5 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-300"><Calendar size={14} className="text-blue-400" /> {d}</div>
                    {e.time && <div className="flex items-center gap-2 text-slate-300"><Clock size={14} className="text-blue-400" /> {e.time}</div>}
                    {e.location && <div className="flex items-center gap-2 text-slate-300"><MapPin size={14} className="text-blue-400" /> {e.location}</div>}
                  </div>
                  <div className="mt-6 pt-5 border-t border-white/5">
                    <div className="label text-center mb-3">Contagem Regressiva</div>
                    <Countdown target={target} />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {!nextEdicao && futureEncontros.length === 0 && (
        <div className="ci-card p-10 mt-10 text-center text-slate-400">Nenhum encontro agendado no momento.</div>
      )}

      {editions.length > 1 && (
        <div className="mt-16">
          <h3 className="font-display text-2xl font-bold mb-6">Todas as edições</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {editions.map((e) => (
              <div key={e.id} className="ci-card p-5">
                <div className="label">{e.edition_number}ª EDIÇÃO</div>
                <h4 className="font-display font-bold mt-2">{e.name}</h4>
                <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Calendar size={12} />{new Date(e.start_date).toLocaleDateString("pt-BR")}</div>
                <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><Users size={12} /> {e.status === "past" ? "Encerrada" : "Próxima"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
